import { useEffect, useMemo, useRef, useState } from 'react';
import { Icon, ICON_SIZE } from '../editor/chrome/icons';
import type { BrandCard } from '../services/work';
import { tileUrl } from '../editor/content/tileUrl';
import { Popover } from '../ui/popover';
import { useRemembered } from './Composer';
import { brandRows, pushRecent, readRecent, type BrandRow } from './brandPickerModel';

/** With this many brands or fewer the list is short enough to read, and there is no search. */
const SEARCH_FROM = 8;

/**
 * The brand a file starts in, chosen from the prompt box. A workspace can hold dozens of
 * brands, and a plain menu ran the height of the window over the page (Farah, 2026-10-23:
 * "fix this project selector"). So: a capped list that scrolls, a search, the brands picked
 * last first, the rest A to Z, and a date beside a name two brands share.
 */
export function BrandPicker({
  open,
  anchor,
  onClose,
  brands,
  current,
  onPick,
}: {
  open: boolean;
  anchor: HTMLElement | null;
  onClose: () => void;
  brands: readonly BrandCard[];
  /** The brand chosen, or null for No Brand. */
  current: string | null;
  onPick: (id: string) => void;
}) {
  return (
    <Popover open={open} anchor={anchor} onClose={onClose} label="Brand" role="dialog" ownsKeyboard offset={6} width={330} className="menu-surface screen-menu flex flex-col overflow-hidden">
      {open ? <Body brands={brands} current={current} onClose={onClose} onPick={onPick} /> : null}
    </Popover>
  );
}

function Body({ brands, current, onClose, onPick }: { brands: readonly BrandCard[]; current: string | null; onClose: () => void; onPick: (id: string) => void }) {
  const [recentRaw, setRecentRaw] = useRemembered('artwork.home.brand-recent');
  const [query, setQuery] = useState('');
  const rows = useMemo(() => brandRows(brands, readRecent(recentRaw), query), [brands, recentRaw, query]);
  const choices = rows.filter((row): row is Extract<BrandRow, { kind: 'brand' }> => row.kind === 'brand');
  const [active, setActive] = useState<string | null>(() => current ?? 'none');
  const search = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const showSearch = brands.length > SEARCH_FROM;

  useEffect(() => {
    const frame = requestAnimationFrame(() => (showSearch ? search.current : list.current)?.focus({ preventScroll: true }));
    return () => cancelAnimationFrame(frame);
  }, [showSearch]);
  // A search lights its first answer, so Return takes it.
  useEffect(() => {
    if (query) setActive(choices[0]?.brand.id ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);
  // The lit row stays in sight while the arrows move it.
  useEffect(() => {
    list.current?.querySelector<HTMLElement>('[data-active]')?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const pick = (id: string) => {
    if (id !== 'none') setRecentRaw(pushRecent(readRecent(recentRaw), id));
    onClose();
    onPick(id);
  };
  const order = [...choices.map((row) => row.brand.id), ...(query ? [] : ['none'])];
  const step = (by: number) => {
    if (order.length === 0) return;
    const at = active === null ? -1 : order.indexOf(active);
    setActive(order[at < 0 ? (by > 0 ? 0 : order.length - 1) : (at + by + order.length) % order.length]!);
  };
  const onKeyDown = (event: React.KeyboardEvent) => {
    event.stopPropagation();
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      step(event.key === 'ArrowDown' ? 1 : -1);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (active !== null && order.includes(active)) pick(active);
    }
  };

  return (
    <div className="flex min-h-0 flex-col" onKeyDown={onKeyDown}>
      {showSearch ? (
        <label className="mx-[6px] mt-[6px] flex h-[34px] shrink-0 items-center gap-[7px] rounded-[8px] bg-fill-1 px-[9px] text-label-3 shadow-[inset_0_0_0_1px_var(--separator)] focus-within:shadow-[inset_0_0_0_1px_var(--accent-text)]">
          <Icon name="search" size={ICON_SIZE.small} />
          <input
            ref={search}
            type="search"
            aria-label="Search brands"
            placeholder="Search brands"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            className="h-full w-0 min-w-0 flex-1 bg-transparent text-[13px] text-label outline-none placeholder:text-label-3 [&::-webkit-search-cancel-button]:hidden"
          />
          {query ? (
            <button type="button" aria-label="Clear search" onClick={() => { setQuery(''); search.current?.focus(); }} className="grid size-[20px] place-items-center rounded-[5px] text-label-2 hover:bg-fill-2 hover:text-label">
              <Icon name="close" size={ICON_SIZE.indicator} />
            </button>
          ) : null}
        </label>
      ) : null}
      <div ref={list} tabIndex={-1} role="listbox" aria-label="Brands" className="ui-scroll menu-list max-h-[320px] min-h-0 overflow-y-auto overscroll-contain outline-none" onPointerLeave={() => setActive(null)}>
        {rows.map((row) =>
          row.kind === 'heading' ? (
            <div key={`h-${row.label}`} className="menu-heading">
              {row.label}
            </div>
          ) : (
            <Row key={`${row.section}-${row.brand.id}`} brand={row.brand} note={row.note} checked={row.brand.id === current} lit={active === row.brand.id} onHover={() => setActive(row.brand.id)} onPick={() => pick(row.brand.id)} />
          ),
        )}
        {query && choices.length === 0 ? <p className="px-[9px] py-[14px] text-center text-[13px] text-label-2">No brands match “{query.trim()}”.</p> : null}
      </div>
      {query ? null : (
        <div className="menu-list shrink-0 border-t border-separator pt-[6px]">
          <Row brand={null} checked={current === null} lit={active === 'none'} onHover={() => setActive('none')} onPick={() => pick('none')} />
        </div>
      )}
    </div>
  );
}

function Row({ brand, note, checked, lit, onHover, onPick }: { brand: BrandCard | null; note?: string; checked: boolean; lit: boolean; onHover: () => void; onPick: () => void }) {
  return (
    <div role="option" aria-selected={checked} data-active={lit || undefined} className="menu-item" onPointerEnter={onHover} onClick={onPick}>
      <span className="menu-check" style={checked ? { color: 'var(--accent-text)' } : undefined}>
        {checked ? <Icon name="check" size={12} /> : null}
      </span>
      <Logo brand={brand} />
      <span className="block min-w-0 flex-1 truncate leading-[18px]">{brand ? brand.name : 'No Brand'}</span>
      {note ? <span className="shrink-0 text-[11px] tabular-nums text-label-3">{note}</span> : null}
    </div>
  );
}

/** The brand's mark on a small white chip, so rows of similar names are told apart at a glance. */
function Logo({ brand }: { brand: BrandCard | null }) {
  const [broken, setBroken] = useState(false);
  if (!brand) return <span aria-hidden className="h-[18px] w-[26px] shrink-0 rounded-[5px] shadow-[inset_0_0_0_1px_var(--separator)]" />;
  if (!brand.logoUrl || broken)
    return (
      <span aria-hidden className="grid h-[18px] w-[26px] shrink-0 place-items-center rounded-[5px] bg-fill-2 text-[10px] font-semibold text-label-2">
        {brand.name.trim().charAt(0).toUpperCase() || '?'}
      </span>
    );
  return (
    <span aria-hidden className="grid h-[18px] w-[26px] shrink-0 place-items-center overflow-hidden rounded-[5px] bg-white shadow-[inset_0_0_0_1px_var(--separator)]">
      <img src={tileUrl(brand.logoUrl)} alt="" draggable={false} loading="lazy" onError={() => setBroken(true)} className="max-h-[12px] max-w-[20px] object-contain" />
    </span>
  );
}
